/**
 * API client that replaces direct Supabase access.
 * All requests go through the Express backend -- the Supabase URL and keys
 * are never exposed to the browser.
 */

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Get the stored access token
 */
const getToken = () => {
  return localStorage.getItem('access_token');
};

/**
 * Store auth tokens
 */
export const setTokens = (accessToken, refreshToken) => {
  if (accessToken) localStorage.setItem('access_token', accessToken);
  if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
};

/**
 * Clear stored tokens
 */
export const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

/**
 * Try refreshing the access token using the stored refresh token
 */
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      return null;
    }

    const data = await response.json();
    setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch {
    clearTokens();
    return null;
  }
};

/**
 * Core fetch wrapper with auth headers, JSON handling, and token refresh
 */
const apiFetch = async (endpoint, options = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // If 401, try refreshing the token once
  if (response.status === 401 && token) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
      });
    }
  }

  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    const seconds = retryAfter ? parseInt(retryAfter, 10) : 60;
    throw new Error(
      `You're doing that too fast. Please wait ${seconds} seconds and try again.`
    );
  }

  return response;
};

/**
 * API methods organized by resource
 */
const api = {
  // --- Auth ---
  auth: {
    login: async (email, password) => {
      const res = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      if (data.access_token) {
        setTokens(data.access_token, data.refresh_token);
      }
      return data;
    },

    register: async (email, password, username, name) => {
      const res = await apiFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, username, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      if (data.access_token) {
        setTokens(data.access_token, data.refresh_token);
      }
      return data;
    },

    logout: async () => {
      try {
        await apiFetch('/auth/logout', { method: 'POST' });
      } catch {
        // Ignore errors - client-side cleanup happens regardless
      }
      clearTokens();
    },

    forgotPassword: async (email) => {
      const res = await apiFetch('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send reset email');
      return data;
    },

    me: async () => {
      const res = await apiFetch('/auth/me');
      if (!res.ok) {
        if (res.status === 401) return null;
        throw new Error('Failed to get user');
      }
      const data = await res.json();
      return data;
    },
  },

  // --- Posts ---
  posts: {
    list: async (offset = 0, limit = 10, userId = null) => {
      const params = new URLSearchParams({ offset, limit });
      if (userId) params.set('user_id', userId);
      const res = await apiFetch(`/posts?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch posts');
      return data.posts;
    },

    create: async (postData) => {
      const res = await apiFetch('/posts', {
        method: 'POST',
        body: JSON.stringify(postData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create post');
      return data.post;
    },

    delete: async (id) => {
      const res = await apiFetch(`/posts/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete post');
      return data;
    },

    like: async (id) => {
      const res = await apiFetch(`/posts/${id}/like`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to like post');
      return data;
    },

    unlike: async (id) => {
      const res = await apiFetch(`/posts/${id}/like`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to unlike post');
      return data;
    },

    addComment: async (postId, content) => {
      const res = await apiFetch(`/posts/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add comment');
      return data.comment;
    },

    deleteComment: async (postId, commentId) => {
      const res = await apiFetch(`/posts/${postId}/comments/${commentId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete comment');
      return data;
    },
  },

  // --- Profiles ---
  profiles: {
    get: async (id) => {
      const res = await apiFetch(`/profiles/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Profile not found');
      return data.profile;
    },

    update: async (id, profileData) => {
      const res = await apiFetch(`/profiles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update profile');
      return data.profile;
    },

    getStats: async (id) => {
      const res = await apiFetch(`/profiles/${id}/stats`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch stats');
      return data.stats;
    },

    getAchievements: async (id, limit = 5) => {
      const res = await apiFetch(`/profiles/${id}/achievements?limit=${limit}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch achievements');
      return data.achievements;
    },

    getUserAchievements: async (id) => {
      const res = await apiFetch(`/profiles/${id}/user-achievements`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch user achievements');
      return data.userAchievements;
    },

    getSettings: async (id) => {
      const res = await apiFetch(`/profiles/${id}/settings`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch settings');
      return data.settings;
    },

    updateSettings: async (id, settings) => {
      const res = await apiFetch(`/profiles/${id}/settings`, {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update settings');
      return data.settings;
    },

    getAllAchievements: async () => {
      const res = await apiFetch('/profiles/data/achievements');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch achievements');
      return data.achievements;
    },
  },

  // --- Runs ---
  runs: {
    list: async (startDate = null, order = 'desc') => {
      const params = new URLSearchParams({ order });
      if (startDate) params.set('start_date', startDate);
      const res = await apiFetch(`/runs?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch runs');
      return data.runs;
    },

    listRoutes: async () => {
      const res = await apiFetch('/runs/routes');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch routes');
      return data.routes;
    },

    create: async (runData) => {
      const res = await apiFetch('/runs', {
        method: 'POST',
        body: JSON.stringify(runData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create run');
      return data.run;
    },

    update: async (id, runData) => {
      const res = await apiFetch(`/runs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(runData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update run');
      return data.run;
    },

    delete: async (id) => {
      const res = await apiFetch(`/runs/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete run');
      return data;
    },
  },

  // --- Social ---
  social: {
    follow: async (userId) => {
      const res = await apiFetch(`/social/follow/${userId}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to follow user');
      return data;
    },

    unfollow: async (userId) => {
      const res = await apiFetch(`/social/follow/${userId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to unfollow user');
      return data;
    },

    getFollowers: async (userId) => {
      const res = await apiFetch(`/social/followers/${userId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch followers');
      return data.followers;
    },

    getFollowing: async (userId) => {
      const res = await apiFetch(`/social/following/${userId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch following');
      return data.following;
    },

    getCounts: async (userId) => {
      const res = await apiFetch(`/social/counts/${userId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch follow counts');
      return data;
    },

    isFollowing: async (userId) => {
      const res = await apiFetch(`/social/is-following/${userId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to check follow status');
      return data.isFollowing;
    },
  },

  // --- Admin ---
  admin: {
    getUsers: async () => {
      const res = await apiFetch('/admin/users');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch users');
      return data.users;
    },

    getPosts: async () => {
      const res = await apiFetch('/admin/posts');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch posts');
      return data.posts;
    },

    getComments: async () => {
      const res = await apiFetch('/admin/comments');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch comments');
      return data.comments;
    },

    deleteUser: async (id) => {
      const res = await apiFetch(`/admin/users/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete user');
      return data;
    },

    deletePost: async (id) => {
      const res = await apiFetch(`/admin/posts/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete post');
      return data;
    },

    deleteComment: async (id) => {
      const res = await apiFetch(`/admin/comments/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete comment');
      return data;
    },
  },
};

export default api;
